/**
 * Building one board: the four canvases, the pen, the eraser, and what each layer
 * paints (docs/03-ARCHITECTURE.md §7.1).
 *
 * Kept beside the React component rather than inside it, because none of it is React:
 * the canvas repaints from the store directly, without the board screen re-rendering
 * around it, which is what keeps a moving hand off the render path.
 */

import type { MutableRefObject } from 'react';

import type { CanvasLayerName } from '@opal/contracts';
import {
  screenWidthToCanvasWidth,
  visibleCanvasRect,
  type Layer,
  type Point,
  type Rect,
  type Viewport,
} from '@opal/shared';

import { drawBackground } from './backgrounds.js';
import { createBoardItems, type BoardItems } from './board-items.js';
import { brushScreenWidth, drawEraserRing, drawSizeRing } from './cursor.js';
import {
  createEraseSession,
  type EraseSession,
  type EraseSessionOptions,
} from './erase-session.js';
import { union } from './ink-board.js';
import { createLaserSession, type LaserSession } from './laser-session.js';
import { smoothInk } from './ink-smooth.js';
import { createShapeRecogniser } from './shape-recognise.js';
import { createSmartPen, type SmartPen } from './smart-pen.js';
import { drawLaser } from './laser-paint.js';
import { createInkSession, type InkSession, type InkSessionOptions } from './ink-session.js';
import { createObjectBoard, type ObjectBoard } from './object-board.js';
import {
  createShapeSession,
  type ShapeSession,
  type ShapeSessionOptions,
} from './shape-session.js';
import { drawMarquee, drawSelectionBox, type SelectStyle } from './select-paint.js';
import { createSelectSession, type SelectSession } from './select-session.js';
import {
  createTextSession,
  type TextSession,
  type TextSessionOptions,
} from './text-session.js';
import { createCanvasLayers, LAYER_ORDER, type CanvasLayers } from './layers.js';
import { inkColor, opacityFor } from './palette.js';
import { createRemoteCursors, type RemoteCursors } from './remote-cursors.js';
import { useBoardStore } from './store.js';
import { createViewportState, toViewport } from './viewport-state.js';

/** Everything the board screen needs to reach once the canvases exist. */
export interface BoardHandle {
  readonly layers: CanvasLayers;
  readonly ink: InkSession;
  /** SRS C4 — the eraser, working on the same ink the pen just put down. */
  readonly erase: EraseSession;
  /** SRS C4 — choosing work already on the board, and changing it. */
  readonly select: SelectSession;
  /** SRS C4 — the shapes on this page. */
  readonly objects: ObjectBoard;
  /** SRS C4 — the laser pointer. Never ink, never saved. */
  readonly laser: LaserSession;
  /** SRS C4 — the tool that draws them. */
  readonly shape: ShapeSession;
  /** SRS C4 — the text tool and the sticky notes, which place objects too. */
  readonly text: TextSession;
  /** SRS C5 — the Smart Pen, turning rough shapes into clean ones after pen-up. */
  readonly smartPen: SmartPen;
  /** Canvas-space box around every stroke and every shape, for Fit to Content. */
  contentBounds(): Rect | null;
  /** Other people's hands. Filled from presence, drawn on the overlay. */
  readonly cursors: RemoteCursors;
  /** Asks for another overlay frame, while remote cursors are still moving. */
  readonly repaintCursors: () => void;
}

/**
 * What the overlay needs to draw the size ring.
 *
 * The colour is kept resolved here rather than looked up while painting: reading a
 * token means touching the DOM, and the ring repaints on every movement of the hand.
 */
export interface RingState {
  at: Point | null;
  color: string;
  /** The eraser's circle is neutral, not the ink colour: it is not going to draw. */
  eraserColor: string;
  /** How the selection box and the lasso are drawn. */
  select: SelectStyle;
}

/** The eraser circle takes a neutral outline from the theme, never the ink colour. */
export const ERASER_RING_TOKEN = '--color-text-secondary';
export const ERASER_RING_FALLBACK = '#71718a';

/** The selection box and the lasso are drawn in the brand colour, over a faint wash. */
export const SELECT_LINE_TOKEN = '--color-primary';
export const SELECT_LINE_FALLBACK = '#3b4ddb';
export const SELECT_WASH = 'rgba(59, 77, 219, 0.08)';


/** How the pen is set up: what it draws with, and what it repaints when it does. */
function inkOptions(userId: string, stack: CanvasLayers): InkSessionOptions {
  return {
    userId,
    targetScreenWidth: () => useBoardStore.getState().targetScreenWidth,
    pen: () => {
      const { tool, color } = useBoardStore.getState();
      const drawing = tool === 'highlighter' ? 'highlighter' : 'pen';
      return { tool: drawing, color: inkColor(color), opacity: opacityFor(drawing) };
    },
    layer: activeLayer,
    // SRS C5 — jitter comes out of every finished stroke while the Smart Pen is on,
    // and none at all while it is off: what the hand drew is what stays.
    smooth: (points) => (useBoardStore.getState().smartPen ? smoothInk(points) : points),
    onChanged: (layer) => {
      stack.scheduler.invalidate(layer);
      // A finished stroke changes how much board there is to scroll through.
      if (layer === 'static') useBoardStore.getState().contentChanged();
    },
  };
}

/** SRS C4 — how the shapes tool is set up. */
function shapeOptions(
  objects: ObjectBoard,
  userId: string,
  stack: CanvasLayers,
): ShapeSessionOptions {
  return {
    objects,
    userId,
    kind: () => useBoardStore.getState().shapeKind,
    sides: () => useBoardStore.getState().polygonSides,
    color: () => inkColor(useBoardStore.getState().color),
    // SRS C3 — a shape is drawn at the width the pen is set to, taken into canvas
    // units at the zoom it was drawn at, exactly as a stroke is.
    canvasWidth: () => {
      const { viewport, targetScreenWidth } = useBoardStore.getState();
      return screenWidthToCanvasWidth(viewport, targetScreenWidth);
    },
    layer: activeLayer,
    onChanged: (layer) => {
      stack.scheduler.invalidate(layer);
    },
  };
}

/** SRS C4 — the laser pointer, which repaints only the overlay it lives on. */
function buildLaser(stack: CanvasLayers): LaserSession {
  return createLaserSession({
    onChanged: () => {
      stack.scheduler.invalidate('overlay');
    },
  });
}

/**
 * SRS C5 — the Smart Pen, watching what the pen finishes.
 *
 * It never sees a stroke before the ink is down, so it cannot get in the way of the
 * hand (docs/03-ARCHITECTURE.md §7.2).
 */
function buildSmartPen(ink: InkSession, objects: ObjectBoard, stack: CanvasLayers): SmartPen {
  const smartPen = createSmartPen({
    recogniser: createShapeRecogniser(),
    // SRS K3 — the administrator's one number, and the teacher's one switch.
    confidence: () => useBoardStore.getState().snapConfidence,
    enabled: () => useBoardStore.getState().smartPen,
    ink,
    objects,
    onChanged: (layer) => {
      stack.scheduler.invalidate(layer);
    },
    // SRS C5 — the screen puts the "Undo snap" pill beside the shape, and takes it
    // away again when the four seconds are up.
    onSnapped: (offer) => {
      useBoardStore.getState().setSnapUndo(offer);
    },
  });
  ink.onFinished((stroke) => {
    smartPen.consider(stroke);
  });
  return smartPen;
}

/** SRS C8 — which layer new work goes on, read the moment the hand goes down. */
function activeLayer(): Layer {
  return useBoardStore.getState().activeLayer;
}

/** SRS C4 — how the text tool and the sticky notes are set up. */
function textOptions(
  objects: ObjectBoard,
  userId: string,
  stack: CanvasLayers,
): TextSessionOptions {
  return {
    objects,
    userId,
    kind: () => (useBoardStore.getState().tool === 'sticky' ? 'sticky' : 'text'),
    style: () => {
      const { color, fontSize, bold, italic, align } = useBoardStore.getState();
      return { color: inkColor(color), fontSize, bold, italic, align };
    },
    paper: () => useBoardStore.getState().stickyColor,
    layer: activeLayer,
    onChanged: (layer) => {
      stack.scheduler.invalidate(layer);
    },
    // The screen puts a real text field over the box; the board says which box.
    onEditing: (object) => {
      useBoardStore.getState().setEditing(object);
    },
  };
}

/** Everything drawn on the page: the ink and the shapes together. */
function boardBounds(ink: InkSession, objects: ObjectBoard): Rect | null {
  const shapes = objects.bounds();
  const drawn = ink.contentBounds();
  return shapes ? union(drawn, shapes) : drawn;
}

/** SRS C4 — how the eraser is set up, on everything the board holds. */
function eraseOptions(items: BoardItems): EraseSessionOptions {
  return {
    ink: items,
    mode: () => useBoardStore.getState().eraserMode,
    // SRS C4 — the circle is a fixed size on screen, so how much board it covers
    // depends on the zoom. The conversion is the one every tool uses (§3.3.1).
    radius: () => {
      const { viewport, eraserScreenWidth } = useBoardStore.getState();
      return screenWidthToCanvasWidth(viewport, eraserScreenWidth) / 2;
    },
  };
}

/** Builds the layer stack, the tools, and paints one through the other. */
export function buildBoard(
  canvases: Map<CanvasLayerName, HTMLCanvasElement>,
  viewport: Viewport,
  ring: MutableRefObject<RingState>,
  userId: string,
): BoardHandle {
  const stack = createCanvasLayers(createViewportState(viewport));
  for (const name of LAYER_ORDER) {
    const canvas = canvases.get(name);
    if (canvas) stack.attach(name, canvas);
  }

  const cursors = createRemoteCursors();
  const ink = createInkSession(inkOptions(userId, stack));
  const objects = createObjectBoard();
  const shape = createShapeSession(shapeOptions(objects, userId, stack));
  const text = createTextSession(textOptions(objects, userId, stack));
  const laser = buildLaser(stack);
  const smartPen = buildSmartPen(ink, objects, stack);

  // Shapes share the board layer with the ink, and paint cannot be lifted off a
  // canvas: a shape that moves or goes means the whole layer is drawn again.
  objects.onChanged(() => {
    ink.invalidateBoard();
    stack.scheduler.invalidate('static');
    useBoardStore.getState().contentChanged();
  });

  // The eraser and the select tool work on one board, whether a piece of it is ink,
  // a shape, a text box or a sticky note.
  const items = createBoardItems(ink, objects);
  const erase = createEraseSession(eraseOptions(items));
  const select = createSelectSession({
    ink: items,
    mode: () => useBoardStore.getState().selectMode,
    zoom: () => useBoardStore.getState().viewport.zoom,
  });

  paintLayers({ stack, ink, objects, shape, cursors, select, laser, smartPen, ring });

  return {
    layers: stack,
    ink,
    objects,
    shape,
    text,
    laser,
    smartPen,
    erase,
    select,
    cursors,
    contentBounds: () => boardBounds(ink, objects),
    repaintCursors: () => {
      stack.scheduler.invalidate('overlay');
    },
  };
}

/** Everything the four canvases draw from. */
interface PaintSources {
  readonly stack: CanvasLayers;
  readonly ink: InkSession;
  readonly objects: ObjectBoard;
  readonly shape: ShapeSession;
  readonly cursors: RemoteCursors;
  readonly select: SelectSession;
  readonly laser: LaserSession;
  readonly smartPen: SmartPen;
  readonly ring: MutableRefObject<RingState>;
}

/** Says what each of the four canvases draws, and what it may leave alone. */
function paintLayers({
  stack,
  ink,
  objects,
  shape,
  cursors,
  select,
  laser,
  smartPen,
  ring,
}: PaintSources): void {
  // The paper colour itself is the element's own CSS background (.canvas-surface);
  // this layer draws the grid or template on top of it (SRS C7).
  stack.setPaint('background', (ctx, view) => {
    drawBackground(ctx, toViewport(view), useBoardStore.getState().background);
  });
  stack.setPaint(
    'static',
    (ctx, view) => {
      ink.paintStatic(ctx, view);
      // SRS C4 — shapes are placed things, so they sit above the ink. Only the ones
      // actually on screen are drawn.
      const viewport = toViewport(view);
      objects.paint(ctx, visibleCanvasRect(viewport), viewport);
    },
    // The session clears this layer itself, so a finished stroke is added to what is
    // already there instead of forcing every stroke on the board to be drawn again.
    { clearFirst: false },
  );
  stack.setPaint(
    'active',
    (ctx, view) => {
      ink.paintActive(ctx, view);
      // The shape being dragged, over the same layer the pen draws its line on.
      shape.paintActive(ctx, view);
    },
    // The session clears this layer itself, which is what lets a long stroke draw only
    // its newest piece each frame.
    { clearFirst: false },
  );

  // docs/06-UI-SPEC.md §15 — the faint ring that shows how wide the next line will
  // be, the eraser's live radius circle, and §15.1 — everyone else's hand, labelled
  // with the tool they are holding.
  stack.setPaint('overlay', (ctx, view) => {
    const state = useBoardStore.getState();
    const now = Date.now();
    // SRS C4 — the laser's trail. Drawn on the overlay and nowhere else, which is
    // what makes it impossible for it to end up on the board or in a saved lecture.
    drawLaser(ctx, toViewport(view), laser.trail(now), now);
    // SRS C5 — a shape on its way from rough ink to clean form. It lives here for
    // 150 ms and nowhere else: the ink is already gone and the shape is not yet down.
    smartPen.paintOverlay(ctx, view);
    if (state.tool === 'eraser') {
      drawEraserRing(ctx, ring.current.at, state.eraserScreenWidth, ring.current.eraserColor);
    } else if (state.tool === 'select') {
      paintSelection(ctx, toViewport(view), select, ring.current.select);
    } else if (state.tool !== 'laser') {
      const width = brushScreenWidth(toViewport(view), state.targetScreenWidth);
      drawSizeRing(ctx, ring.current.at, width, ring.current.color);
    }
    cursors.paint(ctx, toViewport(view), now);
  });
}

/** SRS C4 — the loop being drawn, or the box around what it caught. */
function paintSelection(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  select: SelectSession,
  style: SelectStyle,
): void {
  const marquee = select.marquee;
  if (marquee) {
    drawMarquee(ctx, marquee, viewport, style, useBoardStore.getState().selectMode === 'rect');
    return;
  }
  const bounds = select.bounds();
  if (bounds) drawSelectionBox(ctx, bounds, viewport, style);
}
